import React, { useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Trash } from "react-feather";
import { formatInTimeZone } from "date-fns-tz";
import { NumberRangeColumnFilter, SelectColumnFilter } from "../../assets/functions.js";
import { domainConfig } from "../../config.js";
import { ColumnSortingTable } from "../Table.js";
import {
    PHOTO_COMPETITION_ACTIVITY_ID,
    PHOTO_SUBMISSION_ACTIVITY_ID,
} from "./ActivityPhotoForm.js";
import { PARTICIPANT_VOTING_ACTIVITY_ID } from "./ActivityVotingForm.js";

const QUIZ_ACTIVITY_ID = 1;
const REPORT_TIME_ZONE = "America/Sao_Paulo";
const PHOTO_STATUS_LABELS = {
    pending: "Pendente",
    approved: "Aprovada",
    rejected: "Rejeitada",
};

const safeJsonParse = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return fallback;
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
};

const normalizeFieldValue = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, "value")) {
        return value.value;
    }
    return value ?? "";
};

const getFormFields = (dataTable) => (
    Array.isArray(dataTable?.json?.form?.fields) ? dataTable.json.form.fields : []
);

const getNameField = (dataTable) => getFormFields(dataTable).find((field) => (
    /^(nome|name)$/i.test(String(field?.label || "").trim())
    || /^(nome|name)$/i.test(String(field?.inputID || "").trim())
)) || getFormFields(dataTable).find((field) => (
    /nome|name/i.test(String(field?.label || field?.inputID || ""))
));

const getUserName = (userData, email, dataTable) => {
    const parsedUserData = safeJsonParse(userData, {});
    const nameField = getNameField(dataTable);
    const configuredName = nameField ? normalizeFieldValue(parsedUserData[nameField.inputID]) : "";
    return configuredName || parsedUserData.name || parsedUserData.nome || email || "Participante";
};

const addUserData = (row, userData, dataTable, prefix = "participant_") => {
    const parsedUserData = safeJsonParse(userData, {});
    getFormFields(dataTable).forEach((field) => {
        row[`${prefix}${field.inputID}`] = normalizeFieldValue(parsedUserData[field.inputID]);
    });
    return row;
};

const formatReportDate = (value) => {
    if (!value) return "";
    const parsedDate = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return String(value);
    return formatInTimeZone(parsedDate, REPORT_TIME_ZONE, "dd/MM/yyyy HH:mm:ss");
};

const formatDuration = (milliseconds) => {
    const numericValue = Number(milliseconds);
    if (!Number.isFinite(numericValue) || numericValue < 0) return "";
    const hours = Math.floor(numericValue / 3600000);
    const minutes = Math.floor((numericValue % 3600000) / 60000);
    const seconds = Math.floor((numericValue % 60000) / 1000);
    const remainingMilliseconds = Math.floor(numericValue % 1000);
    const base = [minutes, seconds, remainingMilliseconds]
        .map((part, index) => String(part).padStart(index === 2 ? 3 : 2, "0"))
        .join(":");
    return hours > 0 ? `${String(hours).padStart(2, "0")}:${base}` : base;
};

export const buildActivityMediaUrl = (path, dataTable = {}) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path) || /^(?:blob|data):/i.test(String(path))) return path;
    if (/^\/\//.test(path)) return `https:${path}`;

    const rawImageServer = String(dataTable?.json?.server?.imageServer || domainConfig.imageServer || "").replace(/\/+$/, "");
    const imageServer = rawImageServer.startsWith("//") ? `https:${rawImageServer}` : rawImageServer;
    const normalizedPath = String(path).replace(/^\/+/, "");
    if (!imageServer) return normalizedPath;

    const eventId = dataTable?.event_id ?? dataTable?.evento_id;
    if (!eventId) return `${imageServer}/${normalizedPath}`;

    const eventPrefix = `plataforma-eventos/${eventId}`;
    if (normalizedPath === eventPrefix || normalizedPath.startsWith(`${eventPrefix}/`)) {
        if (imageServer.endsWith(eventPrefix)) {
            return `${imageServer}/${normalizedPath.slice(eventPrefix.length).replace(/^\/+/, "")}`;
        }
        return `${imageServer}/${normalizedPath}`;
    }
    if (imageServer.endsWith(eventPrefix)) return `${imageServer}/${normalizedPath}`;
    return `${imageServer}/${eventPrefix}/${normalizedPath}`;
};

const normalizeOrders = (answer) => {
    const values = Array.isArray(answer?.orders) ? answer.orders : [answer?.order];
    return values.reduce((orders, value) => {
        const numericValue = Number(value);
        if (value !== null && value !== undefined && value !== "" && Number.isInteger(numericValue) && !orders.includes(numericValue)) {
            orders.push(numericValue);
        }
        return orders;
    }, []);
};

const getQuizQuestionCount = (activityJson) => {
    const questions = Array.isArray(activityJson?.questions) ? activityJson.questions : [];
    const configuredCount = Number(activityJson?.selectQuestions?.value);
    if (activityJson?.selectQuestions?.status && Number.isInteger(configuredCount) && configuredCount > 0) {
        return Math.min(configuredCount, questions.length);
    }
    return questions.length;
};

const isQuizHistoryCompleted = (history, answerCount, totalQuestions) => {
    const progressStatus = history?.progress?.status;
    if (typeof progressStatus === "string" && progressStatus.trim()) {
        return progressStatus === "completed";
    }
    return totalQuestions > 0 && answerCount >= totalQuestions;
};

const normalizeScoreMultiplier = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 1;
    return Math.min(5, Math.max(1, Number(numericValue.toFixed(2))));
};

const formatScoreMultiplier = (value) => (
    `${normalizeScoreMultiplier(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}\u00D7`
);

const normalizeQuizRows = (activity, dataTable) => {
    const activityJson = safeJsonParse(activity?.json, {});
    const questions = Array.isArray(activityJson.questions) ? activityJson.questions : [];
    const totalQuestions = getQuizQuestionCount(activityJson);

    return [...(Array.isArray(activity?.history) ? activity.history : [])].map((historyRow) => {
        const resultJson = safeJsonParse(historyRow.history_json ?? historyRow.json, {});
        const answers = Array.isArray(resultJson.answers) ? resultJson.answers.filter(Boolean) : [];
        const completed = isQuizHistoryCompleted(resultJson, answers.length, totalQuestions);
        const completionDate = completed
            ? resultJson?.progress?.completed_at || resultJson.completed_score_date || historyRow.date
            : "";
        const rawScore = Number(resultJson.value);
        const completedScore = resultJson.completed_score_verified === true
            ? Number(resultJson.completed_score)
            : rawScore;
        const storedMultiplier = Number(resultJson.completed_score_multiplier);
        const inferredMultiplier = rawScore > 0 && Number.isFinite(completedScore)
            ? completedScore / rawScore
            : 1;
        const scoreMultiplier = completed
            && resultJson.completed_score_verified === true
            ? normalizeScoreMultiplier(Number.isFinite(storedMultiplier) ? storedMultiplier : inferredMultiplier)
            : 1;
        const row = addUserData({
            ...historyRow,
            participant_name: getUserName(historyRow.user_data, historyRow.email, dataTable),
            completed,
            status: completed ? "Concluído" : "Em andamento",
            score: completed && Number.isFinite(completedScore) ? completedScore : null,
            score_multiplier: completed ? scoreMultiplier : 1,
            score_multiplier_label: completed ? formatScoreMultiplier(scoreMultiplier) : "-",
            time_ms: Number(resultJson.time || 0),
            time_formatted: formatDuration(resultJson.time),
            current_question: `${answers.length}/${totalQuestions}`,
            last_activity_at: formatReportDate(historyRow.date || resultJson?.progress?.last_saved_at),
            completed_at: formatReportDate(completionDate),
        }, historyRow.user_data, dataTable);

        answers.forEach((answer) => {
            if (!answer) return;
            if (answer.skipped === true) {
                row["answer_" + answer.question_id] = "Questão perdida";
                return;
            }
            const question = questions.find((item) => Number(item.question_id) === Number(answer.question_id));
            const selectedOrders = normalizeOrders(answer);
            const selectedAnswerText = (Array.isArray(question?.answers) ? question.answers : [])
                .filter((item) => selectedOrders.includes(Number(item.order)))
                .map((item) => item.text)
                .filter(Boolean)
                .join(", ");
            const typedAnswer = typeof answer.answer_text === "string" ? answer.answer_text.trim() : "";
            row["answer_" + answer.question_id] = typedAnswer || selectedAnswerText;
        });
        return row;
    }).sort((firstRow, secondRow) => (
        Number(secondRow.completed) - Number(firstRow.completed)
        || (firstRow.completed && secondRow.completed ? secondRow.score - firstRow.score : 0)
        || Number(secondRow.current_question.split("/")[0]) - Number(firstRow.current_question.split("/")[0])
        || (firstRow.time_ms || Number.POSITIVE_INFINITY) - (secondRow.time_ms || Number.POSITIVE_INFINITY)
    ));
};

const normalizePhotoRows = (activity, dataTable) => {
    const rows = (Array.isArray(activity?.history) ? activity.history : []).map((historyRow) => {
        const mediaPath = historyRow.media_path || historyRow.storage_path || historyRow.file_name || "";
        const scoreAwarded = Number(historyRow.activity_score_awarded ?? historyRow.score_awarded) === 1;
        const scoreValue = scoreAwarded ? Number(historyRow.activity_score_value || 0) : 0;
        const scoreMultiplier = scoreAwarded ? normalizeScoreMultiplier(historyRow.activity_score_multiplier) : 1;
        return addUserData({
            ...historyRow,
            participant_name: getUserName(historyRow.user_data, historyRow.email, dataTable),
            submission_status: PHOTO_STATUS_LABELS[historyRow.status] || historyRow.status || "",
            attempt: Number(historyRow.attempt || 0),
            submitted_at_formatted: formatReportDate(historyRow.submitted_at),
            reviewed_at_formatted: formatReportDate(historyRow.reviewed_at),
            vote_count: Number(historyRow.vote_count || 0),
            winner_label: Number(historyRow.is_winner) === 1 ? "Sim" : "Não",
            photo_url: buildActivityMediaUrl(mediaPath, dataTable),
            score_awarded_label: scoreAwarded ? "Sim" : "Não",
            score_value: scoreValue,
            score_multiplier: scoreMultiplier,
            score_multiplier_label: scoreAwarded ? formatScoreMultiplier(scoreMultiplier) : "-",
        }, historyRow.user_data, dataTable);
    });
    if (Number(activity?.app_id) !== PHOTO_COMPETITION_ACTIVITY_ID) return rows;
    return rows.sort((firstRow, secondRow) => (
        secondRow.vote_count - firstRow.vote_count
        || String(secondRow.submitted_at || "").localeCompare(String(firstRow.submitted_at || ""))
        || Number(firstRow.user_id || 0) - Number(secondRow.user_id || 0)
    ));
};

const normalizeVotingRows = (activity, dataTable) => (
    (Array.isArray(activity?.history) ? activity.history : []).map((historyRow) => addUserData({
        ...historyRow,
        participant_name: getUserName(historyRow.user_data, historyRow.email, dataTable),
        vote_count: Number(historyRow.vote_count || 0),
        winner_label: Number(historyRow.is_winner) === 1 ? "Sim" : "Não",
    }, historyRow.user_data, dataTable)).sort((firstRow, secondRow) => (
        secondRow.vote_count - firstRow.vote_count
        || String(firstRow.participant_name || "").localeCompare(String(secondRow.participant_name || ""), "pt-BR")
        || Number(firstRow.user_id || 0) - Number(secondRow.user_id || 0)
    ))
).map((row) => {
    const scoreAwarded = Number(row.activity_score_awarded) === 1;
    const scoreValue = scoreAwarded ? Number(row.activity_score_value || 0) : 0;
    const scoreMultiplier = scoreAwarded ? normalizeScoreMultiplier(row.activity_score_multiplier) : 1;
    return {
        ...row,
        score_awarded_label: scoreAwarded ? "Sim" : "Não",
        score_value: scoreValue,
        score_multiplier: scoreMultiplier,
        score_multiplier_label: scoreAwarded ? formatScoreMultiplier(scoreMultiplier) : "-",
    };
});

export const prepareVoteDetailRows = (votes, dataTable) => (
    (Array.isArray(votes) ? votes : []).map((vote) => addUserData({
        ...vote,
        participant_name: getUserName(vote.user_data, vote.email, dataTable),
        vote_at_formatted: formatReportDate(vote.created_at),
    }, vote.user_data, dataTable))
);

export const prepareActivityReportRows = (activity, dataTable) => {
    switch (Number(activity?.app_id)) {
        case QUIZ_ACTIVITY_ID:
            return normalizeQuizRows(activity, dataTable);
        case PHOTO_SUBMISSION_ACTIVITY_ID:
        case PHOTO_COMPETITION_ACTIVITY_ID:
            return normalizePhotoRows(activity, dataTable);
        case PARTICIPANT_VOTING_ACTIVITY_ID:
            return normalizeVotingRows(activity, dataTable);
        default:
            return [];
    }
};

const renderArrayValue = ({ value }) => {
    if (!Array.isArray(value)) return value || "";
    return value.join(", ");
};

const buildUserColumns = (dataTable, showDetails, {
    accessorPrefix = "participant_",
    headerPrefix = "",
    emailAccessor = "email",
    nameAccessor = "participant_name",
} = {}) => {
    const columns = [{ Header: `${headerPrefix}Nome`, accessor: nameAccessor }];
    const showEmail = showDetails || dataTable?.json?.form?.showEmailInUserReport;
    if (showEmail) {
        columns.push({
            Header: `${headerPrefix}${dataTable?.json?.form?.emailLabel || "E-mail"}`,
            accessor: emailAccessor,
        });
    }

    const nameField = getNameField(dataTable);
    getFormFields(dataTable).forEach((field) => {
        if (String(field.inputID) === String(nameField?.inputID)) return;
        if (!showDetails && !field.showInUserReport) return;
        const label = String(field.label || field.inputID || "Campo");
        const column = {
            Header: `${headerPrefix}${label.length > 40 ? `${label.slice(0, 37)}...` : label}`,
            accessor: `${accessorPrefix}${field.inputID}`,
            Cell: renderArrayValue,
        };
        if (field.validation === "number") {
            column.Filter = NumberRangeColumnFilter;
            column.filter = "between";
        } else if (["checkbox", "select"].includes(field.type)) {
            column.Filter = SelectColumnFilter;
            column.filter = "equals";
        }
        columns.push(column);
    });
    return columns;
};

const buildDeleteColumn = (onDeleteResult) => !onDeleteResult ? [] : [{
    Header: "",
    accessor: "icons",
    disableSortBy: true,
    disableExport: true,
    Filter: false,
    Cell: ({ row }) => (
        <OverlayTrigger overlay={<Tooltip>Excluir dados deste participante</Tooltip>}>
            <Trash
                role="button"
                aria-label="Excluir dados deste participante"
                className="align-middle cursor-pointer me-2"
                onClick={() => onDeleteResult(row.original)}
                size={18}
            />
        </OverlayTrigger>
    ),
}];

const buildQuizColumns = (activity, dataTable, showDetails, onDeleteResult) => {
    const activityJson = safeJsonParse(activity?.json, {});
    const columns = [
        ...buildUserColumns(dataTable, showDetails),
        { Header: "Status", accessor: "status", Filter: SelectColumnFilter, filter: "equals" },
        { Header: "Pontuação", accessor: "score", Filter: NumberRangeColumnFilter, filter: "between" },
        { Header: "Tempo", accessor: "time_formatted" },
        { Header: "Progresso", accessor: "current_question", Filter: SelectColumnFilter, filter: "equals" },
        { Header: "Última atividade", accessor: "last_activity_at" },
        { Header: "Conclusão", accessor: "completed_at" },
    ];

    if (showDetails) {
        (Array.isArray(activityJson.questions) ? activityJson.questions : []).forEach((question) => {
            columns.push({
                Header: question.question || `Questão ${question.question_id}`,
                accessor: `answer_${question.question_id}`,
                Filter: SelectColumnFilter,
                filter: "equals",
            });
        });
    }

    if (showDetails) {
        columns.push({ Header: "Multiplicador", accessor: "score_multiplier_label", Filter: SelectColumnFilter, filter: "equals" });
    }
    return [...columns, ...buildDeleteColumn(onDeleteResult)];
};

const buildPhotoColumns = (activity, dataTable, showDetails, onOpenMedia, onOpenVoteDetails, onDeleteResult) => {
    const isCompetition = Number(activity?.app_id) === PHOTO_COMPETITION_ACTIVITY_ID;
    const columns = [];
    if (showDetails) {
        columns.push({ Header: "ID da submissão", accessor: "submission_id", Filter: NumberRangeColumnFilter, filter: "between" });
    }
    columns.push(
        ...buildUserColumns(dataTable, showDetails),
        {
            Header: "Foto",
            accessor: "photo_url",
            Filter: false,
            Cell: ({ value, row }) => value ? (
                <a href={value} onClick={(event) => { event.preventDefault(); onOpenMedia?.(value, row.original); }}>Abrir foto</a>
            ) : "",
            exportValue: (row) => row.photo_url,
        },
        { Header: "Status", accessor: "submission_status", Filter: SelectColumnFilter, filter: "equals" },
        { Header: "Tentativa", accessor: "attempt", Filter: NumberRangeColumnFilter, filter: "between" },
        { Header: "Enviada em", accessor: "submitted_at_formatted" },
        { Header: "Revisada em", accessor: "reviewed_at_formatted" },
        { Header: "Motivo da rejeição", accessor: "rejection_reason" },
    );

    if (isCompetition) {
        columns.push(
            { Header: "Votos", accessor: "vote_count", Filter: NumberRangeColumnFilter, filter: "between" },
            { Header: "Vencedora", accessor: "winner_label", Filter: SelectColumnFilter, filter: "equals" },
            {
                Header: "",
                accessor: "vote_details",
                disableSortBy: true,
                disableExport: true,
                Filter: false,
                Cell: ({ row }) => (
                    <button type="button" className="btn btn-link p-0" onClick={() => onOpenVoteDetails?.(row.original)}>
                        Ver detalhes
                    </button>
                ),
            },
        );
    }
    if (showDetails) {
        columns.push({ Header: "Pontuação concedida", accessor: "score_awarded_label", Filter: SelectColumnFilter, filter: "equals" });
    }
    if (showDetails) {
        columns.push(
            { Header: "Valor da pontuação", accessor: "score_value", Filter: NumberRangeColumnFilter, filter: "between" },
            { Header: "Multiplicador", accessor: "score_multiplier_label", Filter: SelectColumnFilter, filter: "equals" },
        );
    }
    return [...columns, ...buildDeleteColumn(onDeleteResult)];
};

const buildVotingColumns = (activity, dataTable, showDetails, onOpenVoteDetails, onDeleteResult) => [
    ...buildUserColumns(dataTable, showDetails),
    ...(activity?.voting_finalized ? [{ Header: "Vencedor", accessor: "winner_label", Filter: SelectColumnFilter, filter: "equals" }] : []),
    { Header: "Votos recebidos", accessor: "vote_count", Filter: NumberRangeColumnFilter, filter: "between" },
    { Header: "Pontuação concedida", accessor: "score_awarded_label", Filter: SelectColumnFilter, filter: "equals" },
    { Header: "Valor da pontuação", accessor: "score_value", Filter: NumberRangeColumnFilter, filter: "between" },
    { Header: "Multiplicador", accessor: "score_multiplier_label", Filter: SelectColumnFilter, filter: "equals" },
    {
        Header: "",
        accessor: "vote_details",
        disableSortBy: true,
        disableExport: true,
        Filter: false,
        Cell: ({ row }) => (
            <button type="button" className="btn btn-link p-0" onClick={() => onOpenVoteDetails?.(row.original)}>
                Ver detalhes
            </button>
        ),
    },
    ...buildDeleteColumn(onDeleteResult),
];

export const getVoteDetailColumns = (dataTable, showDetails) => [
    ...buildUserColumns(dataTable, showDetails),
    { Header: "Data do voto", accessor: "vote_at_formatted" },
];

export const getActivityReportColumns = (
    activity,
    dataTable,
    showDetails,
    onDeleteResult,
    onOpenMedia,
    onOpenVoteDetails,
) => {
    switch (Number(activity?.app_id)) {
        case QUIZ_ACTIVITY_ID:
            return buildQuizColumns(activity, dataTable, showDetails, onDeleteResult);
        case PHOTO_SUBMISSION_ACTIVITY_ID:
        case PHOTO_COMPETITION_ACTIVITY_ID:
            return buildPhotoColumns(activity, dataTable, showDetails, onOpenMedia, onOpenVoteDetails, onDeleteResult);
        case PARTICIPANT_VOTING_ACTIVITY_ID:
            return buildVotingColumns(activity, dataTable, showDetails, onOpenVoteDetails, onDeleteResult);
        default:
            return [];
    }
};


const ActivityReport = ({
    activity,
    dataTable,
    showDetails,
    triggerExport,
    setTriggerExport,
    onDeleteResult,
    onOpenMedia,
    onOpenVoteDetails,
}) => {
    const rows = useMemo(
        () => prepareActivityReportRows(activity, dataTable),
        [activity, dataTable],
    );
    const columns = useMemo(
        () => getActivityReportColumns(
            activity,
            dataTable,
            showDetails,
            onDeleteResult,
            onOpenMedia,
            onOpenVoteDetails,
        ),
        [activity, dataTable, showDetails, onDeleteResult, onOpenMedia, onOpenVoteDetails],
    );

    return (
        <ColumnSortingTable
            columns={columns}
            title={`${dataTable.title} - Atividades - ${activity?.title || "Sem título"}`}
            data={rows}
            getCellProps={(cellinfo) => ({
                className: cellinfo.column.id === "icons" ? "table-icons-triple" : "",
            })}
            removeCard={true}
            resetTrigger={`${activity?.id || "activity"}-${showDetails ? "details" : "compact"}`}
            triggerExport={triggerExport}
            setTriggerExport={setTriggerExport}
        />
    );
};

export default ActivityReport;
